import { FC, useEffect, useState } from 'react';
import { Col, Form, Grid, Row } from 'rsuite';
import { useFormik, yupToFormErrors } from 'formik';
import { confirm } from '@rsuite/interactions';
import { useCampaignStore } from 'store/campaignStore';

import { Section } from 'components/Rsuite/Section';
import { RsButton } from 'components/Rsuite/Button';
import { BaseCreativeManager } from 'components/Rsuite/CreativeManager';
import useBaseForm from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useBaseForm';
import useDisplayForm from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useDisplayForm';
import useDisplayValidation from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useDisplayValidation';
import { MaxBid } from './Display/MaxBid';
import { RsInput } from 'components/Rsuite/Input';
import { BudgetChoice } from 'containers/RtbCampaigns/RtbCampaignSinglePage/Components/Display/BudgetChoice';
import styles from './baseForm.module.scss';
import FutureDateRangePicker from 'components/DateRangePicker/FutureDateRangePicker';
import moment from 'moment';
import _ from 'lodash';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager,
} from 'core/rtbCampaign/RtbCampaignManager';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { AgeRangeSelection } from './Display/AgeRangeSelection';
import { RsBasicRaio } from 'components/Rsuite/Radio';
import { SegmentsSelection } from './Display/SegmentsSelection';
import { Gender } from 'core/rtbCampaign/singlePage/display';
import { useCoreContext } from 'contexts/coreContext';
import { RsDateRangePicker } from 'components/Rsuite/DateRangePicker';
import { ActionType } from 'core/rtbCampaign/singlePage/common';

interface DisplayFormProps {
  defaultForm: any;
}

const campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager();
const ageOptions = new Array(53).fill(null).map((_, i) => {
  const value = i + 13;
  return {
    value,
    label: value === 65 ? '65+' : value.toString(),
  };
});

const genderOptions = [
  {
    label: '全部',
    value: Gender.ALL,
  },
  {
    label: '男性',
    value: '1',
    isGroup: false,
  },
  {
    label: '女性',
    value: '2',
    isGroup: false,
  },
];

const DisplayForm: FC<DisplayFormProps> = ({ defaultForm }) => {
  const history = useHistory();
  const core = useCoreContext();

  const { actionType, loading, handleLoading } = useCampaignStore();
  // get defaultForm.limitation include age_min value
  const ageMin = _.get(defaultForm, 'limitations.include', []).find(
    (limitation: any) => limitation.type === 'age_min',
  )?.value;
  // get defaultForm.limitation include age_max value
  const ageMax = _.get(defaultForm, 'limitations.include', []).find(
    (limitation: any) => limitation.type === 'age_max',
  )?.value;

  const defaultGender = _.get(defaultForm, 'limitations.include', []).find(
    (limitation: any) => limitation.type === 'gender',
  )?.value[0].value;
  const [ageRange, setAgeRange] = useState<[number, number]>([ageMin, ageMax]);
  const [gender, setGender] = useState<string>(
    defaultGender ? defaultGender : Gender.ALL,
  );

  const [includedDefaultTypes, setIncludedDefaultTypes] = useState<string[]>(
    [],
  );
  const [excludedDefaultTypes, setExcludedDefaultTypes] = useState<string[]>(
    [],
  );

  const convert = (data) => {
    // 使用物件來做分組
    const groups = {};

    data?.forEach((item) => {
      // 如果目前還沒有這個 type 的分組，則先建立
      if (!groups[item.type]) {
        groups[item.type] = {
          op: item.op,
          type: item.type,
          value: [],
        };
      }
      // 將 label 與 value 放入對應的分組中
      groups[item.type].value.push({
        label: item.label,
        value: item.value.split('___')[1],
      });
    });

    // 回傳轉換後的資料陣列
    return Object.values(groups) || [];
  };

  const handleAgeRangeChange = (newAgeRange: [number, number]) => {
    setAgeRange(newAgeRange);
  };

  const handleGenderChange = (newGender: string) => {
    setGender(newGender);
  };

  // init Formik
  const { values, setFieldValue, setErrors, handleSubmit } = useFormik({
    initialValues: {
      ...defaultForm,
    },
    onSubmit: async (values) => {
      const _included = convert(values.basic?.included || []);
      // return
      const _excluded = convert(values.basic?.excluded || []);

      const campaign = {
        ...values,
        limitations: {
          include: [
            {
              op: 'inc',
              type: 'age_min',
              value: ageRange[0],
            },
            {
              op: 'inc',
              type: 'age_max',
              value: ageRange[1],
            },
            ..._included,
          ],
          exclude: [..._excluded],
        },
      };
      if (gender !== Gender.ALL) {
        campaign.limitations.include.push({
          type: 'gender',
          value: genderOptions.filter((item) => item.value === gender),
        });
      }
      // console.log('submitCampaign', campaign);

      // return
      validationSchema
        .validate(values, { abortEarly: false })
        .then(async function () {
          handleLoading(true);
          try {
            switch (actionType) {
              case 'edit':
                await campaignManager.updateCampaign(
                  campaign,
                  order.l1Objects[0]?.l1ObjectId,
                );
                toast.success('成功');
                history.push(`/orders/${order?.orderNumber}`);
                break;
              case 'create':
              case 'copy':
                const createdCampaign = await campaignManager.createCampaign(
                  campaign,
                  order.l1Objects[0]?.l1ObjectId,
                );
                toast.success('成功');
                handleLoading(false);
                const result = await confirm(
                  '活動建立完成，是否繼續編輯當前活動的素材?',
                  {
                    okButtonText: '是',
                    cancelButtonText: '否',
                  },
                );

                if (result) {
                  history.push(
                    `/orders/${order?.orderNumber}/campaigns/${createdCampaign}/edit`,
                  );
                } else {
                  history.push(`/orders/${order?.orderNumber}`);
                }
                break;
              default:
                break;
            }
          } catch (error) {
            console.error('Failed to create campaign:', error);
            handleLoading(false);
          }
        })
        .catch(function (err) {
          setErrors(yupToFormErrors(err));
          console.log('errors', yupToFormErrors(err));
        });
    },
  });

  // 提交表單
  const onSubmit = () => {
    handleSubmit();
  };

  const { campaignMinDate, handleCampaignNameChange, handleBack } = useBaseForm(
    {
      values,
      setFieldValue,
    },
  );

  const {
    order,
    diffDays,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
    handleBudgetTypeChange,
    handleBudgetChange,
    handleChangeBidPrice,
    handleChangeIncluded,
    handleChangeExcluded,
  } = useDisplayForm({
    defaultForm,
    values,
    setFieldValue,
  });

  const { errors, validationSchema, handleValidation } = useDisplayValidation({
    defaultForm,
    newForm: values,
  });

  // 轉換 TreeSelect 的資料格式
  const normalizeTreeSelectValue = (items) =>
    items.map((item) => ({
      id: item.value,
      value: item.value,
      label: item.label,
    }));

  // default form 只會在 EditPage & CreatePage 設置,
  // 這個 useEffect 用來設定修改 campaign 時那些受眾類型要優先去打 API
  // 這樣做的原因是 antD TreeSelect 需要先有 options 資料才能正確 show 資料
  useEffect(() => {
    if (actionType === 'edit' || actionType === 'copy') {
      const defaultList = ['device', 'spaceType', 'momoSegment'];
      const arr = defaultList.filter((item) => {
        return (
          core?.accountManager?.localeMeta?.addonFeatures?.campaignLimitation[
            item
          ] || item === 'momoSegment'
        );
      });
      // 需要預先打 API 撈資料的類型
      const includedDefaultTypes =
        defaultForm.limitations?.include
          // filter 是因為可能包還其他的 type
          ?.filter((item) => arr.includes(item.type))
          ?.map((item) => item.type) || [];
      // 以選中的下拉選單資料
      const _included = defaultForm.limitations?.include
        // filter 是因為可能包還其他的 type
        ?.filter((item) => arr.includes(item.type))
        ?.reduce((prev, curr) => {
          curr.value?.forEach((item) => {
            prev.push({
              op: curr.op,
              type: curr.type,
              label: item.label,
              title: item.label,
              value: curr.type + '___' + item.value,
            });
          });
          return prev;
        }, []);

      // 告訴 UI Component 要先撈取那些 API
      setIncludedDefaultTypes(includedDefaultTypes);
      // setIncluded(_included);
      handleChangeIncluded(_included);

      // 需要預先打 API 撈資料的類型
      const excludedDefaultTypes =
        defaultForm.limitations?.exclude
          // filter 是因為可能包還其他的 type
          ?.filter((item) => arr.includes(item.type))
          ?.map((item) => item.type) || [];

      const _excluded = defaultForm.limitations?.exclude
        // filter 是因為可能包還其他的 type
        ?.filter((item) => arr.includes(item.type))
        ?.reduce((prev, curr) => {
          curr.value?.forEach((item) => {
            prev.push({
              op: curr.op,
              type: curr.type,
              label: item.label,
              title: item.label,
              value: curr.type + '___' + item.value,
            });
          });
          return prev;
        }, []);
      // 告訴 UI Component 要先撈取那些 API
      setExcludedDefaultTypes(excludedDefaultTypes);
      // setExcluded(_excluded);
      handleChangeExcluded(_excluded);

      setAgeRange([defaultForm?.basic?.ageMin, defaultForm?.basic?.ageMax]);
    }

    handleLoading(false);
  }, [
    defaultForm,
    core?.accountManager?.localeMeta?.addonFeatures?.campaignLimitation,
  ]);

  useEffect(() => {
    handleValidation();
  }, [values]);

  return (
    <>
      {loading && <LoadingIndicator />}
      <Section title='廣告基本設定與出價'>
        <Form layout='horizontal' className='w-[1260px]'>
          <Form.Group>
            <Form.ControlLabel>活動名稱</Form.ControlLabel>
            <RsInput
              value={values.basic?.name}
              onChange={handleCampaignNameChange}
              className='!w-80'
              errorMessage={errors?.basic?.name}
            />
          </Form.Group>
          <Form.Group controlId='data-range-radio'>
            <Form.ControlLabel>廣告期間</Form.ControlLabel>
            <div className='relative float-left'>
              <RsDateRangePicker
                startDate={moment(values.basic.startDate).format(
                  'YYYY-MM-DD HH:mm',
                )}
                endDate={moment(values.basic.endDate).format(
                  'YYYY-MM-DD HH:mm',
                )}
                minDate={moment(campaignMinDate).format('YYYY-MM-DD HH:mm')}
                maxDate={moment(order.endDate)
                  .endOf('day')
                  .format('YYYY-MM-DD HH:mm')}
                showTimePicker={true}
                onStartDateChange={handleChangeStartDate}
                onEndDateChange={handleChangeEndDate}
                startDatePickerDisabled={
                  actionType === ActionType.EDIT &&
                  new Date(defaultForm.basic.startDate) <= new Date()
                }
              />
              <div className='flex items-center gap-1'>
                <Form.HelpText>
                  建議活動至少大於兩周，以達到系統最佳學習效果
                  <br />
                  廣告活動開始後，將無法修改開始日期
                  <br />共 {diffDays} 天
                </Form.HelpText>
              </div>
              <Form.ErrorMessage
                show={
                  !![errors?.basic?.startDate, errors?.basic?.endDate]
                    .filter(Boolean)
                    .join(' / ')
                }
                placement='topEnd'
              >
                {[errors?.basic?.startDate, errors?.basic?.endDate]
                  .filter(Boolean)
                  .join(' / ')}
              </Form.ErrorMessage>
            </div>
          </Form.Group>
          <Form.Group controlId='data-range-radio'>
            <Form.ControlLabel>廣告預算</Form.ControlLabel>
            <BudgetChoice
              budgetType={values.basic.budgetType}
              onBudgetTypeChange={handleBudgetTypeChange}
              budget={values.basic.budget}
              dailyTargetBudget={values.basic.dailyTargetBudget}
              onBudgetChange={handleBudgetChange}
              dailyErrorMessage={errors?.basic?.dailyTargetBudget}
              totalErrorMessage={errors?.basic?.budget}
              className={styles.budgets}
              actionType={actionType}
              disabled={false}
            />
          </Form.Group>
        </Form>
      </Section>
      {actionType === 'edit' && (
        <Section title='素材設定'>
          <BaseCreativeManager campaignId={defaultForm.basic.id} />
        </Section>
      )}
      <Section title='受眾選擇與出價'>
        <Form layout='horizontal'>
          <Form.Group>
            <Form.ControlLabel>目標受眾</Form.ControlLabel>
            <div className='flex flex-col bg-[#F7F6FF] px-[24px]'>
              <Grid className='flex flex-col gap-[18px]'>
                <Row className='text-[16px]'>投放指定受眾</Row>
                <Row className='flex items-center' gutter={18}>
                  <Col>年齡</Col>
                  <Col>
                    <AgeRangeSelection
                      options={ageOptions}
                      ageRange={ageRange}
                      onAgeRangeChange={handleAgeRangeChange}
                    />
                  </Col>
                </Row>

                <Row className='flex items-center' gutter={18}>
                  <Col>性別</Col>
                  <Col>
                    <RsBasicRaio
                      data={genderOptions}
                      selected={gender}
                      onChange={handleGenderChange}
                      inline
                    />
                  </Col>
                </Row>
                <Row className='flex items-center' gutter={18}>
                  <Col>類型</Col>
                  <Col>
                    <SegmentsSelection
                      op='inc'
                      defaultTypes={includedDefaultTypes}
                      value={normalizeTreeSelectValue(
                        values.basic?.included || [],
                      )}
                      opposite={normalizeTreeSelectValue(
                        values.basic?.excluded || [],
                      )}
                      onChange={handleChangeIncluded}
                    />
                  </Col>
                </Row>
                <Row className='mt-[12px] text-[16px]'>排除指定受眾</Row>
                <Row className='flex items-center' gutter={18}>
                  <Col>類型</Col>
                  <Col>
                    <SegmentsSelection
                      op='exc'
                      defaultTypes={excludedDefaultTypes}
                      value={normalizeTreeSelectValue(
                        values.basic?.excluded || [],
                      )}
                      opposite={normalizeTreeSelectValue(
                        values.basic?.included || [],
                      )}
                      onChange={handleChangeExcluded}
                    />
                  </Col>
                </Row>
              </Grid>
            </div>
          </Form.Group>
          <Form.Group className='w-[37rem]'>
            <Form.ControlLabel>出價上限</Form.ControlLabel>
            <MaxBid
              maxBid={values.basic.bidPrice}
              currency={order.currency}
              onMaxBidChange={handleChangeBidPrice}
              MaxBidError={errors?.basic?.bidPrice}
            />
          </Form.Group>
        </Form>
      </Section>
      <div className='my-8 ml-8 flex items-center gap-4'>
        <RsButton
          appearance='primary'
          onClick={onSubmit}
          disabled={!_.isEmpty(errors)}
        >
          完成
        </RsButton>
        <RsButton appearance='ghost' onClick={handleBack}>
          取消
        </RsButton>
      </div>
    </>
  );
};

export default DisplayForm;
